import { useCallback, useEffect, useRef, useState } from "react";

type CheckApi = {
  list: () => Promise<string[]>;
  set: (itemKey: string, checked: boolean) => Promise<unknown>;
};

/**
 * Server-persisted checked state for grocery lists.
 * Same interface as useLocalStorageSet: returns [Set<string>, toggle].
 * Optimistic — updates local state immediately, rolls back on error.
 */
export function useGroceryChecks(api: CheckApi): [Set<string>, (key: string) => void] {
  const [set, setSet] = useState<Set<string>>(new Set());
  const apiRef = useRef(api);

  useEffect(() => {
    apiRef.current = api;
  });

  useEffect(() => {
    apiRef.current.list().then(
      (keys) => setSet(new Set(keys)),
      () => {}, // swallow — empty set on error
    );
  }, [api.list]);

  const toggle = useCallback((key: string) => {
    setSet((prev) => {
      const next = new Set(prev);
      const wasChecked = next.has(key);
      if (wasChecked) next.delete(key);
      else next.add(key);

      // Fire-and-forget with rollback on failure
      apiRef.current.set(key, !wasChecked).catch(() => {
        setSet((curr) => {
          const reverted = new Set(curr);
          if (wasChecked) reverted.add(key);
          else reverted.delete(key);
          return reverted;
        });
      });

      return next;
    });
  }, []);

  return [set, toggle];
}
